import React, { useEffect, useMemo } from 'react';

import ErrorBoundary from '@components/ErrorBoundary';

import { TRIM_TOOL } from '@constants/trim-tool';

import ivclass from '@invideoio/web-shared/Modules/IVClass';

import { deviceStateSelector } from '@store/device-state';
import { useAppSelector } from '@store/hook';
import {
  AmplitudeEvents,
  sendAmplitudeAnalyticEvent,
  sendRudderStackAnalyticEvent,
} from '@utils/analytics';
import {
  AnalyticsProductType,
  ClickCtaType,
  ClickCtaUxSource,
} from 'src/types/analytics';
import { ToolsLandingPageResponse } from '../../types/landing-page';

import { localStorageWithExpiry } from '@utils/localStorageUtils';
import { BaseAppType } from 'src/types';

import AppPromoBanner from '@components/AppPromoBanner';
import FeatureSection from '@components/Feature';
import GuideSection from '@components/Guide';
import HeroSection from '@components/Hero';
import PromotionSection from '@components/Promotion';
import DiscoverMoreChips from '@components/discover-more/DiscoverMoreChips';
import HeroSectionV2 from '@components/hero-section';
import TutorialVideo from '@components/tutorial-video/TutorialVideo';
import WorkflowV2 from '@components/workflow-v2';
import { AUTH_TAB } from '@invideoio/web-shared/components/Auth/constants';
import FAQ from '@invideoio/web-shared/components/Faq';
import BannerSlot from '@components/banner-slot';
import Footer from '@invideoio/web-shared/components/Footer';
import Header from '@invideoio/web-shared/components/Header';
import {
  BrowserType,
  PlatformType,
} from '@invideoio/web-shared/constants/common';
import FooterConfig from '@invideoio/web-shared/constants/footer';
import HeaderConfig from '@invideoio/web-shared/constants/header';
import DiscoverBlogs from '@invideoio/web-shared/sections/DiscoverBlogs';
import { AppType } from '@invideoio/web-shared/types/Common';
import { BASE_APP_TYPE_LOADED } from '@store/shell-state/shellState';
import { useDispatch } from 'react-redux';

interface LandingPageContainerProps extends React.PropsWithChildren {
  landingpageState: ToolsLandingPageResponse;
  user?: any;
  socialLinks?: any;
  allowEditor: boolean;
  browser: BrowserType;
  platform: PlatformType;
}

function LandingPageContainer(props: LandingPageContainerProps) {
  const { landingpageState, user, socialLinks, allowEditor, browser } = props;

  const deviceState = useAppSelector(deviceStateSelector);

  const { platform, os } = deviceState;

  const {
    ToolsHeroSection,
    GuideCards,
    WhyToUseSection,
    FAQSection,
    FAQItems,
    BottomSection,
    discorverMore,
    heroSection,
    invideo_blogs,
    blogs,
    hideEmailOnMobile,
    BaseAppType: baseAppType,
  } = landingpageState;

  const dispatch = useDispatch();

  const { MENU, HEADER_LINKS } = HeaderConfig;

  const CTAUrl = heroSection?.CTAUrl;

  const { icons } = TRIM_TOOL.FAQ;

  const onLogin = () => {
    handleCTAClick({
      ux_source: ClickCtaUxSource.TopRightHeader,
      type: ClickCtaType.ClickLogin,
      tab: AUTH_TAB.LOGIN,
      isAuthClick: true,
    });
  };

  // oa search param is used in ai.invideo.io/signup?oa=false to decide
  // whether to show open app button or hide
  const goToAiSignup = ({
    page,
    oa,
    ctaURL,
  }: { page?: string; oa?: string; ctaURL?: string } = {}) => {
    if (ctaURL?.match(/\/(s|i|l)\//)) {
      window.open(appendTrackingParams(ctaURL), '_self');
      return;
    }

    let baseURL = `${process.env.NEXT_PUBLIC_AI_HOST}`;
    baseURL = baseURL + (page === 'login' ? '/login' : '/signup');

    const url = new URL(appendTrackingParams(baseURL));
    if (oa) {
      url.searchParams.append('oa', oa);
    }
    window.location.href = url.toString();
  };

  const appendTrackingParams = (baseURL: string) => {
    const url = new URL(baseURL);
    const qs = localStorageWithExpiry.get('qs');
    const domain_referrer = localStorageWithExpiry.get('domain_referrer');

    if (qs) {
      url.search = qs.startsWith('?') ? qs : `?${qs}`;
    }
    if (domain_referrer) {
      url.searchParams.append('dr', domain_referrer);
    }
    return url.toString();
  };

  const handleCTAClick = ({
    ux_source = ClickCtaUxSource.TopRightHeader,
    type = ClickCtaType.ClickSignup,
    btn_position = 'Right Top',
    link = CTAUrl,
    tab = AUTH_TAB.SIGN_UP,
    isAuthClick = false,
    oa,
  }: {
    ux_source?: ClickCtaUxSource | undefined;
    type?: ClickCtaType | undefined;
    btn_position?: string | undefined;
    link?: string | undefined;
    tab?: AUTH_TAB | undefined;
    isAuthClick?: boolean | undefined;
    oa?: string;
  }) => {
    let amplitudeSource = AmplitudeEvents.clickSignUp;

    if (tab === AUTH_TAB.LOGIN) {
      type = ClickCtaType.ClickLogin;
      amplitudeSource = AmplitudeEvents.clickLogin;
    }

    if (isAuthClick) {
      sendAmplitudeAnalyticEvent(amplitudeSource, {
        btnPosition: btn_position,
      });
      sendRudderStackAnalyticEvent({
        event_name: 'click_cta',
        event_properties: {
          ux_source: ux_source,
          type: type,
          feature_utm: 'na',
          product:
            baseAppType === BaseAppType.AI
              ? AnalyticsProductType.AI
              : AnalyticsProductType.STUDIO,
        },
      });
    }

    if (baseAppType === BaseAppType.AI) {
      if (tab === AUTH_TAB.LOGIN) {
        goToAiSignup({ page: 'login', oa, ctaURL: link });
      } else {
        goToAiSignup({ oa, ctaURL: link });
      }
      return;
    }

    const url = new URL(
      process.env.NEXT_PUBLIC_STUDIO_HOST || 'https://studio.invideo.io',
    );

    if (!isAuthClick && (!link || allowEditor)) {
      const url = new URL(
        window.location.pathname + 'editor',
        process.env.NEXT_PUBLIC_TOOLS_HOST,
      );
      window.open(url.toString(), '_self');
    } else {
      if (!link) {
        const linkUrl = new URL(
          window.location.pathname,
          process.env.NEXT_PUBLIC_BASE_HOST,
        );

        if (linkUrl.pathname.startsWith('/templates')) {
          linkUrl.hostname =
            process.env.NEXT_PUBLIC_BASE_HOST || 'https://invideo.io';

          url.searchParams.append('otherUrl', linkUrl.toString());
        } else {
          url.searchParams.append('otherUrl', linkUrl.toString());
        }
      } else {
        if (link.includes('screen-recorder')) {
          const linkUrl = new URL(
            window.location.pathname + 'recordings/',
            process.env.NEXT_PUBLIC_TOOLS_HOST,
          );
          url.searchParams.append('otherUrl', linkUrl.toString());
        } else {
          url.searchParams.append('otherUrl', link.toString());
        }
      }

      if (tab === AUTH_TAB.LOGIN) {
        url.pathname = '/login';
      } else if (tab === AUTH_TAB.SIGN_UP) {
        url.pathname = '/signup';
      }

      if (tab === AUTH_TAB.LOGIN || tab === AUTH_TAB.SIGN_UP) {
        const returnUrl = new URL(
          'tools/redirect/',
          process.env.NEXT_PUBLIC_TOOLS_HOST,
        );
        url.searchParams.append('forwardToken', 'true');
        const secondRedirect =
          allowEditor && platform === PlatformType.Desktop
            ? new URL(
              window.location.pathname + 'editor/',
              process.env.NEXT_PUBLIC_TOOLS_HOST,
            )
            : url.searchParams.get('otherUrl');

        url.searchParams.delete('otherUrl');
        url.searchParams.append('otherUrl', returnUrl.toString());
        if (secondRedirect) {
          url.searchParams.append('secondRedirect', secondRedirect.toString());
        }
      }

      if (btn_position) {
        url.searchParams.append('btn_position', btn_position);
      }

      window.open(url.toString(), '_self');
    }
  };

  const isAiPage = useMemo(() => baseAppType === BaseAppType.AI, [baseAppType]);

  const redirectLogoLink = () => {
    if (isAiPage) {
      return `${process.env.NEXT_PUBLIC_BASE_HOST}/`;
    }
    return !!user
      ? HEADER_LINKS.logoRedirection.loggedInURL
      : HEADER_LINKS.logoRedirection.nonLoggedInURL;
  };

  useEffect(() => {
    dispatch(BASE_APP_TYPE_LOADED(baseAppType));
  }, []);

  return (
    <React.Fragment>
      <ErrorBoundary>
        <AppPromoBanner os={os} platform={platform} />
      </ErrorBoundary>
      <ErrorBoundary>
        {!isAiPage && <BannerSlot browser={browser} />}
      </ErrorBoundary>
      <ErrorBoundary>
        <div
          className={ivclass(
            'xl:iv-max-w-[1440px]',
            'iv-container',
            'iv-mx-auto',
          )}
        >
          <Header
            onLogin={onLogin}
            onSignUp={() =>
              handleCTAClick({
                isAuthClick: true,
              })
            }
            os={os}
            platform={platform}
            appType={AppType.Tools}
            menu={isAiPage ? HeaderConfig.MENU_AI : HeaderConfig.MENU_V2}
            logoRedirectURL={redirectLogoLink()}
            socialLinks={socialLinks}
            newBrandLogo={true}
            logoURL={
              isAiPage
                ? `${process.env.NEXT_PUBLIC_SHARED_CDN_URL}/logos/invideo-ai-light.svg`
                : undefined
            }
            authFunctions={{
              onAILogin: handleCTAClick,
              onAISignUp: handleCTAClick,
              onStudioLogin: handleCTAClick,
              onStudioSignUp: handleCTAClick,
            }}
          />
        </div>
      </ErrorBoundary>

      <hr className={ivclass('iv-border-t iv-border-solid iv-border-grey-5')} />

      {heroSection && (
        <ErrorBoundary>
          <HeroSectionV2
            browser={browser}
            platform={platform}
            heroSection={heroSection}
            allowEditor={allowEditor}
            hideEmailOnMobile={hideEmailOnMobile}
            onSignUp={handleCTAClick}
          />
        </ErrorBoundary>
      )}

      {!heroSection && ToolsHeroSection && (
        <ErrorBoundary>
          <HeroSection
            platform={platform}
            browser={browser}
            heroSection={ToolsHeroSection}
          />
        </ErrorBoundary>
      )}

      {GuideCards && (
        <ErrorBoundary>
          <GuideSection
            platform={platform as PlatformType}
            browser={browser}
            cards={GuideCards}
            allowEditor={allowEditor}
            onSignUp={handleCTAClick}
          />
        </ErrorBoundary>
      )}

      {landingpageState.MakeWorkflow && (
        <div className="iv-container iv-mx-auto">
          <WorkflowV2
            platform={platform}
            browser={browser}
            makeWorkFlow={landingpageState.MakeWorkflow}
            isAiPage={isAiPage}
          />
        </div>
      )}

      {landingpageState.tutorialVideo && (
        <ErrorBoundary>
          <TutorialVideo
            browser={browser}
            platform={platform}
            videoData={landingpageState.tutorialVideo}
          />
        </ErrorBoundary>
      )}

      {WhyToUseSection && (
        <ErrorBoundary>
          <FeatureSection
            featureSection={WhyToUseSection}
            platform={platform}
            browser={browser}
          />
        </ErrorBoundary>
      )}

      {FAQItems && (
        <ErrorBoundary>
          <div className={ivclass('')}>
            <FAQ
              items={FAQItems}
              title={FAQSection.title}
              platform={platform}
              icons={icons}
            />
          </div>
        </ErrorBoundary>
      )}

      {discorverMore && (
        <ErrorBoundary>
          <DiscoverMoreChips discoverMore={discorverMore} />
        </ErrorBoundary>
      )}

      {invideo_blogs && blogs && (
        <ErrorBoundary>
          <DiscoverBlogs
            blogs={invideo_blogs}
            blogHeading={blogs}
            platform={platform}
            browser={browser}
          />
        </ErrorBoundary>
      )}

      {BottomSection && (
        <ErrorBoundary>
          <PromotionSection
            promotionSection={BottomSection}
            platform={platform}
            browser={browser}
            onSignUp={handleCTAClick}
          />
        </ErrorBoundary>
      )}
      <ErrorBoundary>
        {socialLinks && (
          <Footer
            footer={FooterConfig.FOOTER}
            os={os}
            platform={platform}
            socialLinks={socialLinks}
          />
        )}
      </ErrorBoundary>
    </React.Fragment>
  );
}

export default LandingPageContainer;
